import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { Link, Typography } from "@mui/material";
import { buildAttachmentIdMap } from "@rubi/utils";
import { useNavigate, useParams } from "react-router";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";

import { getServiceConfig } from "@/common/serviceConfigurations";
import { mailTo } from "@/common/utils";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { Permission } from "@/components/UserManagement/types";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";

import { defaultTheme } from "./constants";
import { OnlineReportingContext } from "./context";
import "survey-core/survey-core.min.css";
import "./ViewReport.css";
import { registerAutocomplete } from "./custom-questions/AddressAutocompleteQuestion/AddressAutocompleteQuestion";
import { registerCurrencyInput } from "./custom-questions/CurrencyInputQuestion/CurrencyInputQuestion";
import { registerDriversLicenseScanner } from "./custom-questions/DriversLicenseScannerQuestion";
import { registerEnhancedFileUpload } from "./custom-questions/EnhancedFileUploadQuestion/EnhancedFileUploadQuestion";
import { registerGasQuantityInput } from "./custom-questions/GasQuantityInputQuestion/GasQuantityInputQuestion";
import { registerNarrativeCanvas } from "./custom-questions/NarrativeCanvasQuestion/NarrativeCanvasQuestion";
import { applyViewVisibility } from "./custom-questions/showInProperty";
import ImageMatchBanner from "./report-view/ImageMatchBanner";
import { createSurveyHandlers } from "./survey-handlers";
import { loadImageData } from "./utils";

const ViewReport = ({ editing = false }: { editing: boolean }) => {
  const navigate = useNavigate();
  const appName = window.location.pathname.split("/")[1];
  const [report, setReport] = useState<{ [key: string]: unknown }>({});
  const [survey, setSurvey] = useState<Model>(new Model());
  const [attachmentIDs, setAttachmentIDs] = useState<{ [key: string]: string }>(
    {},
  );
  const saveDraftTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const { id, orgId } = useParams();
  const { getSubmissionForID, getSurveyForID } = useContext(
    OnlineReportingContext,
  );
  const { currentUser } = useContext(AuthStateContext);
  const { isDevEnv } = useDevEnvironment();

  // Intel submitters access their own CISO-org drafts without report_v2
  // staff permissions; the API enforces access on the CISO path.
  const isIntelSubmitter =
    appName === "rubiintel" &&
    currentUser?.permissions.some(
      (permission) => permission.title === Permission.INTEL,
    );

  const canViewIndividualReport =
    isIntelSubmitter ||
    currentUser?.permissions.some(
      (permission) =>
        permission.title === Permission.REPORT_V2_VIEW_INDIVIDUAL_REPORT ||
        permission.title === Permission.REPORT_V2_VIEW_REPORTS,
    );

  const organizationConfig = getServiceConfig(
    orgId ?? currentUser?.rubi_org_id ?? "NA",
    isDevEnv,
  );

  const handlers = createSurveyHandlers({
    id: id ?? "",
    appName,
    attachmentIDs,
    setAttachmentIDs,
    organizationConfig,
    navigate,
  });

  const performSaveDraft = useCallback(async () => {
    if (!survey || !id || !handlers.saveDraft) return;
    try {
      await handlers.saveDraft(survey);
    } catch (err) {
      console.error("Error saving draft:", err);
    } finally {
      setIsSavingDraft(false);
    }
  }, [survey, id, handlers]);

  const saveDraft = useCallback(() => {
    if (!id || isSavingDraft) return;
    if (saveDraftTimeoutRef.current) {
      clearTimeout(saveDraftTimeoutRef.current);
    }
    const timeout = setTimeout(() => {
      setIsSavingDraft(true);
      performSaveDraft();
    }, 500);
    saveDraftTimeoutRef.current = timeout;
  }, [id, isSavingDraft, performSaveDraft]);

  useEffect(() => {
    survey?.onComplete.clear();
    survey?.onComplete.add(async (sender, options) => {
      const success = await handlers.submitForm(sender, options);
      if (success && editing && orgId && id && appName === "rubiintel") {
        await new Promise((r) => setTimeout(r, 2000));
        navigate(`/rubiintel/${orgId}/report/${id}/complete`);
      }
    });
    survey?.onUploadFiles.clear();
    survey?.onUploadFiles.add((survey, options) =>
      handlers.uploadFiles(survey, options),
    );
    survey?.onClearFiles.clear();
    survey?.onClearFiles.add((_, options) => handlers.clearFiles(options));
    survey?.onCurrentPageChanged.clear();
    survey?.onCurrentPageChanged.add(() => {
      if (editing) {
        saveDraft();
      }
      window.scrollTo(0, 0);
    });
  }, [survey, handlers, editing, saveDraft, navigate, orgId, id]);

  useEffect(() => {
    return () => {
      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    async function getReportData() {
      if (getSubmissionForID && getSurveyForID) {
        const result = await getSubmissionForID(id ?? "");
        const surveyResult = await getSurveyForID(result?.survey_id ?? "");
        const submission = result?.submission ?? {};
        await loadImageData(
          submission,
          result?.attachments ?? [],
          true,
          id ?? "",
        );
        setReport(submission);
        const newSurvey = new Model(surveyResult?.survey.configuration);
        newSurvey.data = submission;

        applyViewVisibility(newSurvey, "main");

        setAttachmentIDs(
          buildAttachmentIdMap(submission, result?.attachments ?? []),
        );
        newSurvey.applyTheme(defaultTheme);

        if (!editing) {
          newSurvey.showPreview();
          newSurvey.showNavigationButtons = false;
        }
        setSurvey(newSurvey);
      }
    }
    registerAutocomplete();
    registerCurrencyInput();
    registerDriversLicenseScanner();
    registerEnhancedFileUpload();
    registerGasQuantityInput();
    registerNarrativeCanvas();
    getReportData();
  }, []);

  if (!canViewIndividualReport) {
    return (
      <DisabledComponentDialog
        isDialogOpen={true}
        handleClose={() => {
          navigate(`/`);
        }}
        title="You do not have access to view this report."
        message={
          <Typography>
            Please contact{" "}
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Requesting Access to View Reports",
                body: `Hello Rubicon Support Team,

                I would like to request access to view reports.

                Thank you.

                Sincerely,

                ${currentUser?.first_name} ${currentUser?.last_name}
                ${currentUser?.email}`,
              })}
              color="secondary"
            >
              support@tryrubicon.com
            </Link>{" "}
            to request access.
          </Typography>
        }
      />
    );
  }

  return (
    <>
      {!editing && <ImageMatchBanner />}
      <Survey model={survey} />
    </>
  );
};

export default ViewReport;
